import { useState } from "react";
import {
  Calendar,
  Clock,
  MapPin,
  Phone,
  Share2,
  Check,
  Sparkles,
  Ticket,
} from "lucide-react";
import { useI18n } from "@project/lib/use-i18n";
import { formatDate } from "@project/lib/date-format";
import { Button } from "@project/components/ui/button";
import { Dialog, DialogContent } from "@project/components/ui/dialog";

export interface EventDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  event: any;
  onRSVP?: (event: any) => void;
}

export function EventDetailModal({
  open,
  onOpenChange,
  event,
  onRSVP,
}: EventDetailModalProps) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);

  if (!event) {
    return null;
  }

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: event.title,
          text: event.short_description ?? event.title,
          url: window.location.href,
        });
        return;
      } catch {
        // Fallback to clipboard
      }
    }
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  // Sample schedule agenda items if none provided
  const agendaItems = event.agenda ?? [
    {
      time: "09:00 AM",
      title: "Welcoming Devotees & Registration",
      desc: "Traditional welcome with Tilak and Aarti.",
    },
    {
      time: "10:30 AM",
      title: "Dharma Pravachan & Bhajan Kirtan",
      desc: "Spiritual discourse by revered Gurudev and elders.",
    },
    {
      time: "12:30 PM",
      title: "Mahaprasad & Bhojanshala Seva",
      desc: "Community lunch prasad for all guests and resident elders.",
    },
    {
      time: "02:30 PM",
      title: "Impact Presentation & Felicitations",
      desc: "Felicitation of community leaders and volunteers.",
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto p-0 sm:rounded-2xl">
        <div className="w-full">
          {/* Event Banner & Overlay */}
          <div className="relative aspect-[16/9] w-full overflow-hidden bg-muted sm:aspect-[21/9]">
            <img
              src={
                event.banner_url ??
                "https://images.unsplash.com/photo-1544717305-2782549b5136?auto=format&fit=crop&w=1200&q=80"
              }
              alt={event.title}
              className="!h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/45 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              <div className="space-y-2">
                <span className="bg-gold/90 inline-flex items-center gap-1 rounded-full px-3 py-0.5 text-xs font-bold uppercase tracking-wider text-gold-foreground">
                  <Sparkles className="h-3 w-3" />{" "}
                  {event.category ?? "Community Event"}
                </span>
                <h2 className="text-2xl font-extrabold !text-white md:text-3xl drop-shadow-md">
                  {event.title}
                </h2>
              </div>
              <Button
                size="sm"
                variant="secondary"
                onClick={handleShare}
                className="w-fit border-white/30 bg-white/20 !text-white backdrop-blur hover:bg-white/30 shrink-0"
              >
                {copied ? (
                  <Check className="mr-1.5 h-4 w-4 text-green-400" />
                ) : (
                  <Share2 className="mr-1.5 h-4 w-4" />
                )}
                {copied
                  ? t("community.events.detailModal.copied")
                  : t("community.events.detailModal.shareEvent")}
              </Button>
            </div>
          </div>

          <div className="space-y-6 p-6">
            {/* Date, Time, Venue Meta Grid */}
            <div className="border-border/80 bg-secondary/30 grid gap-3 rounded-xl border p-4 sm:grid-cols-3">
              <div className="flex items-start gap-3">
                <div className="bg-gold/15 grid h-10 w-10 shrink-0 place-items-center rounded-lg text-gold">
                  <Calendar className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Date</p>
                  <p className="text-sm font-semibold text-foreground">
                    {event.event_date ? formatDate(event.event_date) : "Upcoming"}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="bg-gold/15 grid h-10 w-10 shrink-0 place-items-center rounded-lg text-gold">
                  <Clock className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Time</p>
                  <p className="text-sm font-semibold text-foreground">
                    {event.start_time ?? "09:00 AM"}{" "}
                    {event.end_time ? `- ${event.end_time}` : ""}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="bg-gold/15 grid h-10 w-10 shrink-0 place-items-center rounded-lg text-gold">
                  <MapPin className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-muted-foreground">Venue</p>
                  <p className="text-sm font-semibold text-foreground break-words">
                    {event.venue ?? "Jivandhara Campus, Palitana"}
                  </p>
                </div>
              </div>
            </div>

            {/* Detailed Description */}
            <div>
              <h3 className="mb-2 text-lg font-bold text-primary">
                About This Event
              </h3>
              <p className="whitespace-pre-line text-sm leading-relaxed text-muted-foreground">
                {event.description ??
                  event.short_description ??
                  "Join us for this community gathering. All members, families, and guests are cordially invited to register and attend."}
              </p>
            </div>

            {/* Event Agenda Timeline */}
            <div>
              <h3 className="mb-3 text-lg font-bold text-primary">
                {t("community.events.detailModal.agenda")}
              </h3>
              <div className="border-gold/40 relative space-y-4 border-l-2 pl-4">
                {agendaItems.map((item: any, idx: number) => (
                  <div key={item.id ?? item.title ?? idx} className="relative">
                    <span className="absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full bg-gold ring-4 ring-background" />
                    <span className="text-xs font-bold text-gold">
                      {item.time}
                    </span>
                    <h4 className="text-sm font-semibold text-foreground">
                      {item.title}
                    </h4>
                    <p className="text-xs text-muted-foreground">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Venue Map & Contact Banner */}
            <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-border bg-card p-4">
              <div className="flex items-center gap-3">
                <Phone className="h-5 w-5 shrink-0 text-gold" />
                <div>
                  <p className="text-xs text-muted-foreground">
                    Organizer Contact
                  </p>
                  <p className="text-sm font-semibold">
                    {event.contact_no ?? "+91 98765 43210"}
                  </p>
                </div>
              </div>
              <a
                href={`https://maps.google.com/?q=${encodeURIComponent(event.venue ?? "Palitana Gujarat")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-xs font-bold text-gold hover:underline"
              >
                <MapPin className="mr-1 h-4 w-4" />{" "}
                {t("community.events.detailModal.getDirections")}
              </a>
            </div>

            {/* CTA Actions */}
            <div className="flex flex-wrap items-center justify-end gap-3 border-t border-border pt-4">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              {onRSVP && (
                <Button
                  className="hover:bg-gold/90 bg-gold font-bold text-gold-foreground"
                  onClick={() => {
                    onOpenChange(false);
                    onRSVP(event);
                  }}
                >
                  <Ticket className="mr-1.5 h-4 w-4" />
                  {t("community.events.rsvpBtn")}
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
